import { useState, useMemo, useCallback, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import ImageWithFallback from '@/components/base/ImageWithFallback';
import CityAutocomplete from '@/components/base/CityAutocomplete';
import { INDIAN_STATES } from '@/constants/indianStates';
import { listingsApi, downloadBlob, auth } from '@/services/api';
import { useApiData } from '@/hooks/useApiData';
import PropertyAuditModal from './components/PropertyAuditModal';
import { mapListingToCard } from '@/services/listingMapper';
import { useLocation } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_PUBLIC_API_URL || '';
const UPLOAD_BASE = import.meta.env.VITE_UPLOAD_BASE_URL || API_BASE;

interface ApiListing {
  id: number;
  title: string;
  category: string;
  status: string;
  approval_status: string | null;
  city: string;
  location: string;
  state_province: string;
  size_acres: number;
  expected_rent: number;
  rent_currency: string;
  rent_period: string;
  images_json: string[] | string | null;
  description: string | null;
  features: string[] | string | null;
  use_cases: string[] | string | null;
  posted_date: string;
  view_count: number;
  is_featured: number;
  is_verified: number;
  show_owner_info: number;
  owner_name: string | null;
  owner_contact: string | null;
  transaction_type: string | null;
  transaction_types: string[] | string | null;
}

interface PropertyRow {
  id: number;
  title: string;
  type: string;
  category: string;
  status: string;
  rawStatus: string;
  approvalStatus: string;
  city: string;
  locality: string;
  stateProvince: string;
  price: number;
  sizeValue: number;
  sizeUnit: string;
  sizeDisplay: string;
  rentDisplay: string;
  images: string[];
  imagePaths: string[];
  description: string;
  features: string[];
  postedDate: string;
  viewCount: number;
  transactionTypes: string[];
  showOwnerInfo: boolean;
  ownerName: string;
  ownerContact: string;
}

const ITEMS_PER_PAGE = 25;

const statusLabel = (s: string) => {
  const map: Record<string, string> = { available: 'Active', under_review: 'Under Review', leased: 'Leased', sold: 'Sold', off_market: 'Off Market' };
  return map[s] || s;
};

const statusBadge = (s: string) => {
  const map: Record<string, string> = {
    available: 'bg-primary-500/15 text-primary-700 border-primary-500/25',
    under_review: 'bg-accent-100 text-accent-700 border-accent-500/25',
    leased: 'bg-teal-500/15 text-teal-400 border-teal-500/25',
    sold: 'bg-rose-500/15 text-rose-400 border-rose-500/25',
    off_market: 'bg-background-200/50 text-foreground-500 border-background-300/30',
  };
  return map[s] || 'bg-background-200/50 text-foreground-500 border-background-300/30';
};

const categoryLabel = (c: string) => {
  const map: Record<string, string> = { commercial: 'Commercial', industrial: 'Industrial', agricultural: 'Agricultural', residential: 'Residential', mixed_use: 'Mixed Use' };
  return map[c] || c;
};

const typeBadge = (t: string) => {
  const map: Record<string, string> = {
    'Commercial': 'bg-blue-500/15 text-blue-400', 'Industrial': 'bg-amber-500/15 text-amber-400',
    'Agricultural': 'bg-teal-500/15 text-teal-400', 'Residential': 'bg-green-500/15 text-green-400',
    'Mixed Use': 'bg-purple-500/15 text-purple-400',
  };
  return map[t] || 'bg-background-200/50 text-foreground-500';
};

function parseCategory(c: string): string {
  const map: Record<string, string> = {
    'Commercial': 'commercial', 'Industrial': 'industrial', 'Agricultural': 'agricultural',
    'Residential': 'residential', 'Mixed Use': 'mixed_use',
  };
  return map[c] || '';
}

function parseStatus(s: string): string {
  const map: Record<string, string> = {
    'Active': 'available', 'Under Review': 'under_review', 'Leased': 'leased',
    'Sold': 'sold', 'Off Market': 'off_market',
  };
  return map[s] || '';
}

function safeParseJson<T>(val: T | string | null): T | null {
  if (!val) return null;
  if (typeof val !== 'string') return val as T;
  try { return JSON.parse(val) as T; } catch { return null; }
}

function mapApiToRow(item: ApiListing): PropertyRow {
  const parsedImages = safeParseJson<string[]>(item.images_json);
  const parsedFeatures = safeParseJson<string[]>(item.features);
  const parsedTransactionTypes = safeParseJson<string[]>(item.transaction_types);
  const rawPaths = Array.isArray(parsedImages)
    ? parsedImages.filter((p): p is string => typeof p === 'string' && p.length > 0)
    : [];

  return {
    id: item.id,
    title: item.title,
    type: categoryLabel(item.category),
    category: item.category,
    status: statusLabel(item.status),
    rawStatus: item.status,
    approvalStatus: item.approval_status || 'approved',
    city: item.city || item.location,
    locality: item.location || '',
    stateProvince: item.state_province || '',
    price: item.expected_rent,
    sizeValue: item.size_acres,
    sizeUnit: 'Acres',
    sizeDisplay: `${item.size_acres} Acres`,
    rentDisplay: '\u20B9' + item.expected_rent.toLocaleString('en-IN') + '/' + (item.rent_period || 'mo'),
    images: buildDashboardImageUrls(rawPaths),
    imagePaths: rawPaths,
    description: item.description || '',
    features: Array.isArray(parsedFeatures) ? parsedFeatures : [],
    postedDate: item.posted_date ? new Date(item.posted_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-',
    viewCount: item.view_count || 0,
    transactionTypes: Array.isArray(parsedTransactionTypes)
      ? parsedTransactionTypes
      : (item.transaction_type ? [item.transaction_type] : []),
    showOwnerInfo: item.show_owner_info === 1,
    ownerName: item.owner_name || '',
    ownerContact: item.owner_contact || '',
  };
}

function buildDashboardImageUrls(paths: string[]): string[] {
  if (!paths || !Array.isArray(paths)) return [];
  const base = UPLOAD_BASE.endsWith('/') ? UPLOAD_BASE.slice(0, -1) : UPLOAD_BASE;
  return paths
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .map((p) => {
      if (p.startsWith('data:')) return p;
      if (p.startsWith('http://') || p.startsWith('https://')) return p;
      if (p.startsWith('/uploads/')) return `${base}${p}`;
      if (p.startsWith('uploads/')) return `${base}/${p}`;
      return '';
    })
    .filter((p) => p.length > 0);
}

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'sale', label: 'Sale' },
  { value: 'jv', label: 'JV' },
  { value: 'lease', label: 'Lease' },
  { value: 'development', label: 'Development' },
  { value: 'rent', label: 'Rent' },
  { value: 'any', label: 'Any' },
];

function normalizeTransactionType(t: string): string {
  const map: Record<string, string> = {
    buy: 'buy',
    sell: 'sale',
    sale: 'sale',
    jv: 'jv',
    lease: 'lease',
    development: 'development',
    rent: 'rent',
    any: 'any',
  };
  return map[t] || t;
}

const emptyForm = {
  title: '', category: '', city: '', location: '', expected_rent: '', size_acres: '',
  description: '', rent_period: 'monthly', rent_currency: 'INR', state_province: '', owner_name: '', owner_contact: '',
  status: 'available',
};

export default function PropertiesPage() {
  const location = useLocation();
  const dashboardFilter = (location.state as Record<string, unknown> | null)?.dashboardFilter as Record<string, string> | undefined;

  const [filters, setFilters] = useState({ type: '', status: dashboardFilter?.status || '', city: '', search: '', sortBy: 'posted_date', sortDir: 'desc', priceMin: '', priceMax: '' });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingRow, setEditingRow] = useState<PropertyRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PropertyRow | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [generatingReport, setGeneratingReport] = useState<number | null>(null);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<string[]>([]);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [ownerToggleId, setOwnerToggleId] = useState<number | null>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditInitialPropertyId, setAuditInitialPropertyId] = useState<number | null>(null);

  // Admin = super_admin/admin/owner → gets delete, approve, toggle & audit.
  // Everyone else can create + edit but cannot delete.
  const userRole = (auth.getRole() || '').toLowerCase();
  const isAdmin = ['super_admin', 'admin', 'owner'].includes(userRole);

  const apiParams = useMemo(() => {
    const p: Record<string, string | number> = { per_page: 200, page: 1 };
    if (filters.type) p.category = filters.type.toLowerCase();
    if (filters.status) {
      const revMap: Record<string, string> = { 'Active': 'available', 'Under Review': 'under_review', 'Leased': 'leased', 'Sold': 'sold', 'Off Market': 'off_market' };
      p.status = revMap[filters.status] || filters.status.toLowerCase();
    }
    return p;
  }, [filters.type, filters.status]);

  const { data: apiResp, loading, refetch } = useApiData<{ data: ApiListing[]; pagination: { total: number } }>(
    () => listingsApi.list(apiParams).then(r => r as unknown as { data: ApiListing[]; pagination: { total: number } }),
    [apiParams, refreshKey]
  );

  const allProperties: PropertyRow[] = useMemo(() => (apiResp?.data || []).map(mapApiToRow), [apiResp]);

  const filtered = useMemo(() => {
    let result = [...allProperties];
    if (filters.city) result = result.filter(p => p.city.toLowerCase().includes(filters.city.toLowerCase()));
    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(p => p.title.toLowerCase().includes(q) || p.city.toLowerCase().includes(q) || String(p.id).includes(q));
    }
    if (filters.priceMin) result = result.filter(p => p.price >= Number(filters.priceMin));
    if (filters.priceMax) result = result.filter(p => p.price <= Number(filters.priceMax));
    result.sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[filters.sortBy] as number | string;
      const bVal = (b as Record<string, unknown>)[filters.sortBy] as number | string;
      const cmp = typeof aVal === 'number' ? (aVal as number) - (bVal as number) : String(aVal).localeCompare(String(bVal));
      return filters.sortDir === 'desc' ? -cmp : cmp;
    });
    return result;
  }, [allProperties, filters]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const toggleSelect = (id: number) => setSelected(s => s.includes(id) ? s.filter(i => i !== id) : [...s, id]);
  const toggleAll = () => selected.length === paginated.length ? setSelected([]) : setSelected(paginated.map(p => p.id));

  const cities = [...new Set(allProperties.map(p => p.city).filter(Boolean))];

  const keptExistingCount = existingImages.filter(p => !removedImages.includes(p)).length;
  const totalImages = keptExistingCount + imagePreviews.length;

  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const keptExistingCount = existingImages.filter(p => !removedImages.includes(p)).length;
    const maxAllowed = Math.max(0, 10 - keptExistingCount);
    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    for (let i = 0; i < files.length && newFiles.length < maxAllowed; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }
    setSelectedImages(prev => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, maxAllowed);
    });
    setImagePreviews(prev => {
      const combined = [...prev, ...newPreviews];
      if (combined.length > maxAllowed) {
        combined.slice(maxAllowed).forEach(url => URL.revokeObjectURL(url));
      }
      return combined.slice(0, maxAllowed);
    });
  };

  const removeImage = (idx: number) => {
    URL.revokeObjectURL(imagePreviews[idx]);
    setSelectedImages(prev => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const openCreate = () => {
    setEditingRow(null);
    setFormData(emptyForm);
    // Clean up any existing previews
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setSelectedImages([]);
    setImagePreviews([]);
    setExistingImages([]);
    setRemovedImages([]);
    setSelectedTransactionTypes([]);
    setShowCreate(true);
  };

  const openEdit = (row: PropertyRow) => {
    setFormData({
      title: row.title,
      category: row.category,
      city: row.city,
      location: row.locality,
      expected_rent: String(row.price),
      size_acres: String(row.sizeValue),
      description: row.description,
      rent_period: 'monthly',
      rent_currency: 'INR',
      state_province: row.stateProvince || '',
      owner_name: row.ownerName || '',
      owner_contact: row.ownerContact || '',
      status: row.rawStatus,
    });
    setEditingRow(row);
    // Fix: imagePaths is already a clean string[] after mapApiToRow
    const cleanPaths = Array.isArray(row.imagePaths) ? row.imagePaths.filter(p => typeof p === 'string' && p.length > 0) : [];
    setExistingImages(cleanPaths);
    setRemovedImages([]);
    setSelectedImages([]);
    setImagePreviews([]);
    setSelectedTransactionTypes((row.transactionTypes || []).map(normalizeTransactionType));
    setShowCreate(true);
  };

  const toggleTransactionType = (val: string) => {
    setSelectedTransactionTypes(prev =>
      prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]
    );
  };

  const handleSubmit = useCallback(async () => {
    if (!formData.title || !formData.category || !formData.city) {
      setActionMsg({ type: 'error', text: 'Title, category, and city are required.' });
      clearMsg();
      return;
    }
    setSubmitting(true);
    try {
      if (editingRow) {
        const baseFields: Record<string, unknown> = {
          title: formData.title,
          category: formData.category,
          city: formData.city,
          location: formData.location,
          expected_rent: Number(formData.expected_rent) || 0,
          size_acres: Number(formData.size_acres) || 0,
          rent_period: formData.rent_period,
          rent_currency: formData.rent_currency,
          state_province: formData.state_province,
          description: formData.description,
          owner_name: formData.owner_name,
          owner_contact: formData.owner_contact,
          status: (formData as Record<string, unknown>).status || editingRow.rawStatus || 'available',
          transaction_type: selectedTransactionTypes[0] || 'lease',
          transaction_types: selectedTransactionTypes,
        };

        const hasImageChanges = selectedImages.length > 0 || removedImages.length > 0;

        // Step 1: Always update fields first when editing
        await listingsApi.update(editingRow.id, baseFields);

        // Step 2: Update images separately if changed
        if (hasImageChanges) {
          const imageFd = new FormData();
          imageFd.append('remove_images', JSON.stringify(removedImages));
          selectedImages.forEach((file) => imageFd.append('images', file));
          await listingsApi.updateWithImages(editingRow.id, imageFd);
        }

        setActionMsg({ type: 'success', text: 'Property updated successfully!' });
      } else {
        const hasImages = selectedImages.length > 0;
        if (hasImages) {
          const fd = new FormData();
          fd.append('title', formData.title);
          fd.append('category', formData.category);
          fd.append('city', formData.city);
          fd.append('location', formData.location);
          fd.append('expected_rent', String(Number(formData.expected_rent) || 0));
          fd.append('size_acres', String(Number(formData.size_acres) || 0));
          fd.append('rent_period', formData.rent_period || 'monthly');
          fd.append('rent_currency', formData.rent_currency || 'INR');
          fd.append('state_province', formData.state_province);
          fd.append('description', formData.description);
          fd.append('owner_name', formData.owner_name);
          fd.append('owner_contact', formData.owner_contact);
          fd.append('status', 'available');
          fd.append('transaction_type', selectedTransactionTypes[0] || 'lease');
          fd.append('transaction_types', JSON.stringify(selectedTransactionTypes));
          selectedImages.forEach((file) => fd.append('images', file));
          await listingsApi.createWithImages(fd);
        } else {
          const payload: Record<string, unknown> = {
            title: formData.title,
            category: formData.category,
            city: formData.city,
            location: formData.location,
            expected_rent: Number(formData.expected_rent) || 0,
            size_acres: Number(formData.size_acres) || 0,
            rent_period: formData.rent_period,
            rent_currency: formData.rent_currency,
            state_province: formData.state_province,
            description: formData.description,
            owner_name: formData.owner_name,
            owner_contact: formData.owner_contact,
            status: 'available',
            transaction_type: selectedTransactionTypes[0] || 'lease',
            transaction_types: selectedTransactionTypes,
          };
          await listingsApi.create(payload);
        }
        setActionMsg({ type: 'success', text: 'Property created successfully!' });
      }
      setShowCreate(false);
      setEditingRow(null);
      setFormData(emptyForm);
      setSelectedImages([]);
      setImagePreviews([]);
      setExistingImages([]);
      setRemovedImages([]);
      setSelectedTransactionTypes([]);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Operation failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [formData, editingRow, selectedImages, removedImages, selectedTransactionTypes]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await listingsApi.delete(deleteTarget.id);
      setActionMsg({ type: 'success', text: `"${deleteTarget.title}" deleted.` });
      setDeleteTarget(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Delete failed.' });
    } finally {
      setSubmitting(false);
      clearMsg();
    }
  }, [deleteTarget]);

  const handleDownloadReport = useCallback(async (property: PropertyRow) => {
    setGeneratingReport(property.id);
    try {
      const { blob, filename } = await listingsApi.downloadReport(property.id);
      downloadBlob(blob, filename || `ai-report-${property.id}.pdf`);
      setActionMsg({ type: 'success', text: `AI report for "${property.title}" downloaded.` });
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Report generation failed.' });
    } finally {
      setGeneratingReport(null);
      clearMsg();
    }
  }, []);

  const handleApprove = useCallback(async (property: PropertyRow, approval: 'approved' | 'rejected') => {
    setApprovingId(property.id);
    try {
      await listingsApi.updateApproval(property.id, approval);
      setActionMsg({ type: 'success', text: `"${property.title}" ${approval === 'approved' ? 'approved and published' : 'rejected'}.` });
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Action failed.' });
    } finally {
      setApprovingId(null);
      clearMsg();
    }
  }, []);

  const handleToggleOwnerInfo = useCallback(async (property: PropertyRow) => {
    setOwnerToggleId(property.id);
    try {
      await listingsApi.updateOwnerVisibility(property.id, !property.showOwnerInfo);
      setActionMsg({ type: 'success', text: `Owner info ${!property.showOwnerInfo ? 'shown' : 'hidden'} for "${property.title}".` });
      setRefreshKey(k => k + 1);
    } catch (err) {
      setActionMsg({ type: 'error', text: err instanceof Error ? err.message : 'Toggle failed.' });
    } finally {
      setOwnerToggleId(null);
      clearMsg();
    }
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground-950">Properties</h1>
            <p className="text-xs text-foreground-500">
              {filtered.length} properties found{loading ? ' (loading...)' : ''}
              {allProperties.filter(p => p.approvalStatus === 'pending').length > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 text-[10px] font-semibold border border-amber-500/30">
                  <i className="ri-time-line" />
                  {allProperties.filter(p => p.approvalStatus === 'pending').length} pending approval
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={openCreate} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
              <i className="ri-add-line" /> Add Property
            </button>
            {isAdmin && (
              <button
                onClick={() => { setAuditInitialPropertyId(null); setShowAudit(true); }}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-background-200/60 bg-white text-foreground-500 hover:text-foreground-950 hover:border-accent-200 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-history-line" /> Audit Log
              </button>
            )}
            {isAdmin && selected.length > 0 && (
              <button
                onClick={async () => {
                  setSubmitting(true);
                  let count = 0;
                  for (const id of selected) {
                    try { await listingsApi.delete(id); count++; } catch { /* skip */ }
                  }
                  setSelected([]);
                  setRefreshKey(k => k + 1);
                  setSubmitting(false);
                  setActionMsg({ type: 'success', text: `${count} properties deleted.` });
                  clearMsg();
                }}
                disabled={submitting}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-rose-500/20 bg-rose-500/10 text-rose-400 text-sm font-semibold hover:bg-rose-500/20 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-delete-bin-line" /> Delete ({selected.length})
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-background-400 text-sm" />
            <input type="text" placeholder="Search by title, city, or ID..." value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }} className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/50" />
          </div>
          <select value={filters.type} onChange={e => { setFilters(f => ({ ...f, type: e.target.value })); setPage(1); }} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer">
            <option value="">Type</option>
            {['Commercial', 'Industrial', 'Agricultural', 'Residential', 'Mixed Use'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer">
            <option value="">Status</option>
            {['Active', 'Under Review', 'Leased', 'Sold', 'Off Market'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={filters.city} onChange={e => { setFilters(f => ({ ...f, city: e.target.value })); setPage(1); }} className="px-3 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer">
            <option value="">City</option>
            {cities.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={() => setFilters({ type: '', status: '', city: '', search: '', sortBy: 'posted_date', sortDir: 'desc', priceMin: '', priceMax: '' })} className="px-3 py-2.5 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">
            <i className="ri-refresh-line" /> Reset
          </button>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
          {loading ? (
            <div className="p-12 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 bg-background-200/60 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60">
                    {isAdmin && (
                      <th className="text-left px-4 py-3">
                        <input type="checkbox" checked={selected.length === paginated.length && paginated.length > 0} onChange={toggleAll} className="w-4 h-4 rounded" />
                      </th>
                    )}
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Property</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Type</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">City</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Rent</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Listed</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Approval</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Owner</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr><td colSpan={isAdmin ? 10 : 9} className="px-4 py-12 text-center text-sm text-foreground-500">No properties found matching your filters.</td></tr>
                  ) : (
                    paginated.map(p => (
                      <tr key={p.id} className="border-b border-background-200/50 hover:bg-background-200/80 transition-colors">
                        <td className="px-4 py-3">
                          {isAdmin && (
                            <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="w-4 h-4 rounded" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-background-200/60 overflow-hidden flex items-center justify-center flex-shrink-0">
                              {p.images.length > 0 ? (
                                <ImageWithFallback
                                  src={p.images[0]}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  fallbackClassName="w-full h-full"
                                />
                              ) : (
                                <i className="ri-building-line text-background-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground-950 truncate max-w-[220px]">{p.title}</p>
                              <p className="text-[11px] text-foreground-500">{p.locality}</p>
                              <p className="text-[11px] text-foreground-400">{p.sizeDisplay}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeBadge(p.type)} whitespace-nowrap`}>{p.type}</span></td>
                        <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">{p.city}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-foreground-950 whitespace-nowrap">{p.rentDisplay}</td>
                        <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusBadge(p.rawStatus)} whitespace-nowrap`}>{p.status}</span></td>
                        <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">
                          <span>{p.postedDate}</span>
                          <span className="text-foreground-400 block text-[10px]">{p.viewCount} views</span>
                        </td>
                        {/* Approval column */}
                        <td className="px-4 py-3">
                          {isAdmin && p.approvalStatus === 'pending' ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleApprove(p, 'approved')}
                                disabled={approvingId === p.id}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-500/10 text-teal-500 border border-teal-500/30 text-[10px] font-semibold hover:bg-teal-500/20 disabled:opacity-40 cursor-pointer whitespace-nowrap"
                                title="Approve & Publish"
                              >
                                <i className={approvingId === p.id ? 'ri-loader-4-line animate-spin' : 'ri-check-line'} />
                                Approve
                              </button>
                              <button
                                onClick={() => handleApprove(p, 'rejected')}
                                disabled={approvingId === p.id}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-500/10 text-rose-400 border border-rose-500/30 text-[10px] font-semibold hover:bg-rose-500/20 disabled:opacity-40 cursor-pointer whitespace-nowrap"
                                title="Reject"
                              >
                                <i className="ri-close-line" />
                                Reject
                              </button>
                            </div>
                          ) : (
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                              p.approvalStatus === 'approved'
                                ? 'bg-teal-500/10 text-teal-500 border-teal-500/30'
                                : p.approvalStatus === 'rejected'
                                ? 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                                : 'bg-accent-100 text-accent-700 border-accent-200'
                            }`}>
                              {p.approvalStatus === 'approved' ? 'Approved' : p.approvalStatus === 'rejected' ? 'Rejected' : 'Pending'}
                            </span>
                          )}
                        </td>
                        {/* Owner info visibility */}
                        <td className="px-4 py-3">
                          {isAdmin ? (
                            <button
                              onClick={() => handleToggleOwnerInfo(p)}
                              disabled={ownerToggleId === p.id}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold border transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap ${
                                p.showOwnerInfo
                                  ? 'bg-primary-500/10 text-primary-700 border-primary-500/30 hover:bg-primary-500/20'
                                  : 'bg-background-100 text-foreground-500 border-background-200/60 hover:bg-background-200/50'
                              }`}
                              title={p.showOwnerInfo ? 'Click to hide owner info on frontend' : 'Click to show owner info on frontend'}
                            >
                              <i className={ownerToggleId === p.id ? 'ri-loader-4-line animate-spin' : p.showOwnerInfo ? 'ri-eye-line' : 'ri-eye-off-line'} />
                              {p.showOwnerInfo ? 'Visible' : 'Hidden'}
                            </button>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-1 rounded-lg border whitespace-nowrap ${
                              p.showOwnerInfo
                                ? 'bg-primary-500/10 text-primary-700 border-primary-500/30'
                                : 'bg-background-100 text-foreground-500 border-background-200/60'
                            }`}>
                              <i className={p.showOwnerInfo ? 'ri-eye-line' : 'ri-eye-off-line'} />
                              {p.showOwnerInfo ? 'Visible' : 'Hidden'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-accent-700 cursor-pointer" title="Edit"><i className="ri-edit-line" /></button>
                            <button onClick={() => handleDownloadReport(p)}
                              disabled={generatingReport === p.id}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                              title="Download AI Report"
                            >
                              <i className={generatingReport === p.id ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-2-line'} />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => { setAuditInitialPropertyId(p.id); setShowAudit(true); }}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-accent-700 cursor-pointer"
                                title="View history"
                              >
                                <i className="ri-history-line" />
                              </button>
                            )}
                            {isAdmin && (
                              <button onClick={() => setDeleteTarget(p)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 hover:text-rose-400 cursor-pointer" title="Delete"><i className="ri-delete-bin-line" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {!loading && paginated.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-background-200/60">
              <p className="text-xs text-foreground-500">Showing {(page - 1) * ITEMS_PER_PAGE + 1}-{Math.min(page * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 disabled:opacity-30 cursor-pointer"><i className="ri-arrow-left-s-line" /></button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => setPage(n)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm cursor-pointer ${n === page ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:bg-background-200/60'}`}>{n}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-background-400 disabled:opacity-30 cursor-pointer"><i className="ri-arrow-right-s-line" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit Slide-out */}
      {showCreate && (
        <div className="fixed inset-0 z-[1000] flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowCreate(false); setEditingRow(null); imagePreviews.forEach(url => URL.revokeObjectURL(url)); setSelectedImages([]); setImagePreviews([]); }} />
          <div className="relative z-10 ml-auto w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-background-200/60 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading text-lg font-bold text-foreground-950">{editingRow ? 'Edit Property' : 'New Property'}</h2>
              <button onClick={() => { setShowCreate(false); setEditingRow(null); imagePreviews.forEach(url => URL.revokeObjectURL(url)); setSelectedImages([]); setImagePreviews([]); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Title *</label>
                <input type="text" placeholder="Property title" value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50" />
              </div>

              {/* Transaction Types Checkboxes */}
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-2">Interested in</label>
                <div className="flex flex-wrap gap-2">
                  {TRANSACTION_TYPE_OPTIONS.map(opt => (
                    <label key={opt.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors select-none text-sm ${selectedTransactionTypes.includes(opt.value) ? 'bg-accent-500/10 border-accent-500/40 text-accent-700 font-semibold' : 'bg-white border-background-200/60 text-foreground-600 hover:border-accent-500/30 hover:bg-accent-500/5'}`}>
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 accent-accent-500"
                        checked={selectedTransactionTypes.includes(opt.value)}
                        onChange={() => toggleTransactionType(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {selectedTransactionTypes.length === 0 && (
                  <p className="text-[11px] text-foreground-400 mt-1.5">Select one or more applicable transaction types</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Category *</label>
                  <select value={formData.category} onChange={e => setFormData(f => ({ ...f, category: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer">
                    <option value="">Select category</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                    <option value="agricultural">Agricultural</option>
                    <option value="residential">Residential</option>
                    <option value="mixed_use">Mixed Use</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Status</label>
                  <select
                    value={(formData as Record<string, unknown>).status as string || (editingRow ? editingRow.rawStatus : 'available')}
                    onChange={e => setFormData(f => ({ ...f, status: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer"
                  >
                    <option value="available">Active</option>
                    <option value="under_review">Under Review</option>
                    <option value="leased">Leased</option>
                    <option value="sold">Sold</option>
                    <option value="off_market">Off Market</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Location</label>
                  <input type="text" placeholder="Full location address" value={formData.location} onChange={e => setFormData(f => ({ ...f, location: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">City *</label>
                  <CityAutocomplete
                    value={formData.city}
                    onChange={(c) => setFormData(f => ({ ...f, city: c }))}
                    onStateChange={(s) => setFormData(f => ({ ...f, state_province: s }))}
                    state={formData.state_province || undefined}
                    name="city"
                    placeholder="Type city name (min 2 letters)..."
                  />
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5 mt-3">State / UT</label>
                  <select
                    name="state_province"
                    value={formData.state_province}
                    onChange={(e) => setFormData(f => ({ ...f, state_province: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer"
                  >
                    <option value="">Select state / UT</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Expected Rent (INR)</label>
                  <input type="number" placeholder="Monthly rent" value={formData.expected_rent} onChange={e => setFormData(f => ({ ...f, expected_rent: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Size (Acres)</label>
                  <input type="number" step="0.01" placeholder="Size in acres" value={formData.size_acres} onChange={e => setFormData(f => ({ ...f, size_acres: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Owner Name</label>
                <input type="text" placeholder="Property owner" value={formData.owner_name} onChange={e => setFormData(f => ({ ...f, owner_name: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">Description</label>
                <textarea rows={4} placeholder="Property description" value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none" />
              </div>

              {/* Image Upload / Management */}
              <div>
                <label className="block text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5">
                  Property Images
                </label>
                <p className="text-xs text-foreground-500 mb-2">
                  {editingRow
                    ? 'Add new photos or remove existing ones, then click Update Property to save.'
                    : 'Upload up to 10 photos. Max 5MB each.'}
                </p>

                {/* Existing images (edit mode) */}
                {editingRow && existingImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {existingImages.map((path) => {
                      if (removedImages.includes(path)) return null;
                      const base = UPLOAD_BASE.endsWith('/') ? UPLOAD_BASE.slice(0, -1) : UPLOAD_BASE;
                      const src = path.startsWith('http') || path.startsWith('data:') ? path : `${base}${path}`;
                      return (
                        <div key={path} className="relative w-20 h-20 rounded-lg overflow-hidden border border-background-200/60 group">
                          <ImageWithFallback
                            src={src}
                            alt="Property"
                            className="w-full h-full object-cover object-top"
                            fallbackClassName="w-full h-full"
                          />
                          <button
                            type="button"
                            onClick={() => setRemovedImages(prev => [...prev, path])}
                            className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-rose-500/90 text-white text-xs hover:bg-rose-600 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                            title="Remove image"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* New image previews */}
                {imagePreviews.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {imagePreviews.map((preview, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-background-200/60 group">
                        <img src={preview} alt={`Preview ${i + 1}`} className="w-full h-full object-cover object-top" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-rose-500/90 text-white text-xs hover:bg-rose-600 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload trigger */}
                <label className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                  totalImages >= 10
                    ? 'border-background-200/60 bg-background-200/30 text-foreground-400 cursor-not-allowed'
                    : 'border-background-200/60 bg-background-50 hover:border-accent-500/40 hover:bg-accent-500/5 text-foreground-500 hover:text-accent-700'
                }`}>
                  <i className="ri-image-add-line text-lg" />
                  <span className="text-sm font-medium">
                    {totalImages === 0 ? 'Choose Images' : `${totalImages}/10 — Add More`}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    disabled={totalImages >= 10}
                    className="hidden"
                  />
                </label>
              </div>

              <button onClick={handleSubmit} disabled={submitting} className="w-full py-3 rounded-xl bg-accent-500 text-white text-sm font-semibold hover:bg-accent-400 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {submitting ? 'Saving...' : editingRow ? 'Update Property' : 'Publish Property'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/15 flex items-center justify-center mb-3">
                <i className="ri-delete-bin-line text-rose-400 text-xl" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">Delete Property?</h3>
              <p className="text-sm text-foreground-500 mt-1">This will permanently delete <strong className="text-foreground-950">{deleteTarget.title}</strong>. This action cannot be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-background-200/60 text-sm text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={handleDelete} disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      <PropertyAuditModal
        open={showAudit}
        onClose={() => setShowAudit(false)}
        properties={allProperties.map(p => ({ id: p.id, title: p.title }))}
        initialPropertyId={auditInitialPropertyId}
      />
    </DashboardLayout>
  );
}